import { computed, ref } from "vue";

/**
 * Um SPLIT de verdade entre dois painéis: arrasta a linha, o painel fica com a
 * largura que a pessoa quiser, lembrada entre sessões.
 *
 * A regra é a que o `OrbeDock` estreou (dono, 20/08: "ben não move split
 * lateral") — este arquivo existe porque o dono pediu o MESMO gesto em mais
 * dois lugares (26/08: lista|conversa do inbox e a coluna do copiloto), e três
 * cópias da mesma aritmética de pointer é como elas divergem.
 *
 * `null` é "nunca arrastou": vale a largura padrão do CSS do painel, que segue
 * responsiva. Pointer capture no próprio handle, como os outros arrastos da
 * casa, para o dedo poder sair da linha fina sem soltar o split. Duplo clique
 * volta ao padrão — o "desfazer" mais barato que há.
 *
 * `anchor` diz de que lado do handle o painel mora: `"left"` (painel à
 * esquerda, arrastar para a direita alarga) ou `"right"` (o contrário — o caso
 * do drawer do Ben).
 */
export function usePaneSplit(options: {
	storageKey: string;
	/** Nunca menor que um painel legível. */
	min: number;
	/** Nunca maior que esta fração da janela. */
	maxFraction?: number;
	anchor: "left" | "right";
	/** Onde o painel está em relação ao handle: ao LADO (o padrão) ou o handle DENTRO dele (a gaveta). */
	pane?: "sibling" | "parent";
}) {
	const { storageKey, min, maxFraction = 0.6, anchor, pane: where = "sibling" } = options;

	const width = ref<number | null>(read());
	const resizing = ref(false);
	let start: { x: number; width: number } | null = null;

	function read(): number | null {
		try {
			const saved = Number(localStorage.getItem(storageKey));
			return Number.isFinite(saved) && saved >= min ? saved : null;
		} catch {
			return null;
		}
	}

	function clamp(px: number): number {
		return Math.min(Math.max(px, min), Math.round(window.innerWidth * maxFraction));
	}

	/** Aplicado no PAINEL. `undefined` enquanto ninguém arrastou. */
	const style = computed(() =>
		width.value !== null ? { width: `${width.value}px`, flexShrink: 0 } : undefined,
	);

	function onDown(event: PointerEvent): void {
		if (event.button !== 0) return;
		const handle = event.currentTarget as HTMLElement;
		handle.setPointerCapture?.(event.pointerId);
		// A largura REAL do vizinho, não a lembrada: o primeiro arrasto parte do
		// que está na tela.
		const pane =
			where === "parent"
				? handle.parentElement
				: anchor === "left"
					? (handle.previousElementSibling as HTMLElement | null)
					: (handle.nextElementSibling as HTMLElement | null);
		start = { x: event.clientX, width: pane?.getBoundingClientRect().width ?? min };
		resizing.value = true;
	}

	function onMove(event: PointerEvent): void {
		if (!start) return;
		const delta = anchor === "left" ? event.clientX - start.x : start.x - event.clientX;
		width.value = clamp(start.width + delta);
	}

	function onUp(): void {
		if (!start) return;
		start = null;
		resizing.value = false;
		try {
			if (width.value !== null) localStorage.setItem(storageKey, String(Math.round(width.value)));
		} catch {
			// Sem quota não se lembra a largura; o arrasto em si já aconteceu.
		}
	}

	function onReset(): void {
		width.value = null;
		try {
			localStorage.removeItem(storageKey);
		} catch {
			// Idem.
		}
	}

	return { width, style, resizing, onDown, onMove, onUp, onReset };
}
